import React, { useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
// The guest widget's checkout describes a booking the same way; New Booking's selections have its shape.
import { describeBookingDateLines, describeSelectionGuests } from "pagesV2/BookingWidget/bookingDescription"
import { getExperienceCoverImage } from "pagesV2/BookingWidget/helpers"
import { MIN_PRICE_SAVING } from "../constants"
import { getAddOnLines, getGuestLineLabel, splitPriceBreakdown } from "../helpers/priceLines"
import styles from "./BookingForm.module.css"

const FoldButton = ({ isOpen, openLabel, closeLabel, onToggle }) => (
  <Button
    type="text"
    shape="circle"
    size="small"
    aria-expanded={isOpen}
    aria-label={isOpen ? closeLabel : openLabel}
    icon={<Icon type="feather" name={isOpen ? "ChevronUp" : "ChevronDown"} width={16} height={16} color="currentColor" />}
    onClick={onToggle}
  />
)

FoldButton.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  openLabel: PropTypes.string.isRequired,
  closeLabel: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired
}

// One line of a price summary: label left, amount right. Step 1's summary (BookingSummary) uses it too.
export const PriceLine = ({ label, amount }) => (
  <Flex justify="space-between" align="baseline" gap="var(--spacing-m)" className={styles.summaryLine}>
    <span>{label}</span>
    <span className={styles.summaryAmount}>{amount}</span>
  </Flex>
)

PriceLine.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired
}

// "$507.92 $434.00": the price before the date's adjustments struck beside it when the date makes
// the booking cheaper. Step 1's summary uses it too.
export const StruckTotal = ({ total, totalBeforeAdjustments }) => (
  <Flex align="center" gap="var(--spacing-xxs)">
    {totalBeforeAdjustments - total > MIN_PRICE_SAVING && (
      <span className={styles.summaryTotalStruck}>{numberToCurrency(totalBeforeAdjustments)}</span>
    )}
    <span>{numberToCurrency(total)}</span>
  </Flex>
)

StruckTotal.propTypes = {
  total: PropTypes.number.isRequired,
  totalBeforeAdjustments: PropTypes.number
}

StruckTotal.defaultProps = {
  totalBeforeAdjustments: 0
}

// An experience's price lines (Figma 5796:176439): its guests, each add-on, a negotiated subtotal and
// the promo code when there are any, the fees the guide lists on their own line, and the rest of
// Taxes & Fees. They add up to the experience's total (before the card processing fee).
const PriceDetails = ({ experience, selections, discountLabel }) => {
  const { t } = useTranslation()
  const { guestLines, listedFees, otherFeesTotal } = splitPriceBreakdown(selections?.breakdown ?? [])
  const addOnLines = getAddOnLines(selections?.addOns, experience?.add_ons)
  const originalBaseAmount = Number(selections?.originalBaseAmount ?? selections?.baseAmount) || 0
  const subtotalAdjustment = (Number(selections?.baseAmount) || 0) - originalBaseAmount
  const listedFeesTotal = listedFees.reduce((sum, item) => sum + (Number(item.rawAmount) || 0), 0)
  // What the promo code takes off, as the rest of the lines leave it: the fees are already worked out
  // on the discounted price, so this is exactly the difference to the total.
  const linesBeforeDiscount =
    originalBaseAmount + subtotalAdjustment + (Number(selections?.addOnsTotal) || 0) + listedFeesTotal + Math.max(0, otherFeesTotal)
  const discount = discountLabel ? linesBeforeDiscount - (Number(selections?.preCardTotal) || 0) : 0

  return (
    <Flex vertical>
      {guestLines.map((item) => (
        <PriceLine key={item.label} label={getGuestLineLabel(item, t)} amount={item.amount} />
      ))}
      {addOnLines.map((line) => (
        <PriceLine
          key={line.key}
          label={t("booking.line_quantity", { label: line.title, count: line.count })}
          amount={numberToCurrency(line.amount)}
        />
      ))}
      {Math.abs(subtotalAdjustment) > MIN_PRICE_SAVING && (
        <PriceLine
          label={t("booking.order_summary.subtotal_adjustment")}
          amount={`${subtotalAdjustment < 0 ? "-" : "+"}${numberToCurrency(Math.abs(subtotalAdjustment))}`}
        />
      )}
      {discount > MIN_PRICE_SAVING && <PriceLine label={discountLabel} amount={`-${numberToCurrency(discount)}`} />}
      {listedFees.map((item) => (
        <PriceLine key={item.label} label={item.label} amount={item.amount} />
      ))}
      {otherFeesTotal > MIN_PRICE_SAVING && <PriceLine label={t("booking.summary.taxes_and_fees")} amount={numberToCurrency(otherFeesTotal)} />}
    </Flex>
  )
}

PriceDetails.propTypes = {
  experience: PropTypes.object,
  selections: PropTypes.object,
  discountLabel: PropTypes.string
}

PriceDetails.defaultProps = {
  experience: null,
  selections: null,
  discountLabel: null
}

// One experience of the order (Figma 5796:176423): photo and title, date and guests, its Price Details
// (open until folded) and its total. An experience the API refused (`error`) says why, with "Change"
// to edit it.
const OrderExperience = ({ experience, selections, discountLabel, error, onChange }) => {
  const { t } = useTranslation()
  const [isPriceOpen, setIsPriceOpen] = useState(true)
  const coverImage = getExperienceCoverImage(experience)
  return (
    <Flex vertical gap="var(--spacing-xs)" className={styles.orderExperience}>
      <Flex align="center" gap="var(--spacing-m)">
        <div className={styles.orderExperienceImage}>{coverImage && <img src={coverImage} alt={experience?.title} />}</div>
        <span className={styles.orderExperienceTitle}>{experience?.title}</span>
      </Flex>
      <Flex vertical>
        <span className={styles.orderExperienceText}>{describeBookingDateLines({ experience, selection: selections, t }).join(" · ")}</span>
        <span className={styles.orderExperienceText}>{describeSelectionGuests({ experience, selection: selections, t })}</span>
      </Flex>
      <Flex vertical gap="var(--spacing-xxs)" className={styles.orderPriceDetails}>
        <Flex align="center" gap="var(--spacing-xs)">
          <span className={styles.orderPriceDetailsTitle}>{t("booking.order_summary.price_details")}</span>
          <FoldButton
            isOpen={isPriceOpen}
            openLabel={t("booking.order_summary.show_price_details")}
            closeLabel={t("booking.order_summary.hide_price_details")}
            onToggle={() => setIsPriceOpen((isOpen) => !isOpen)}
          />
        </Flex>
        {isPriceOpen && <PriceDetails experience={experience} selections={selections} discountLabel={discountLabel} />}
      </Flex>
      <Flex justify="flex-end" className={styles.orderExperienceTotal}>
        <StruckTotal
          total={Number(selections?.preCardTotal ?? selections?.amount) || 0}
          totalBeforeAdjustments={Number(selections?.preCardTotalBeforeAdjustments) || 0}
        />
      </Flex>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            onChange && (
              <Button type="link" size="small" onClick={onChange}>
                {t("booking.added_experiences.change")}
              </Button>
            )
          }
        />
      )}
    </Flex>
  )
}

OrderExperience.propTypes = {
  experience: PropTypes.object,
  selections: PropTypes.object,
  discountLabel: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func
}

OrderExperience.defaultProps = {
  experience: null,
  selections: null,
  discountLabel: null,
  error: null,
  onChange: null
}

// New Booking step 2's "Order Summary" (redesign, Figma 5796:176417): its experiences, folded under
// the header until opened (or while one has an error), then the order's promo code and total
// (`footer`), always shown.
const OrderSummary = ({ items, footer, onChangeItem }) => {
  const { t } = useTranslation()
  const [isFolded, setIsFolded] = useState(true)
  const isExpanded = !isFolded || items.some((item) => item.error)
  return (
    <Flex vertical gap="var(--spacing-s)" className={styles.stepSection}>
      <Flex align="center" gap="var(--spacing-s)">
        <span className={styles.stepSectionTitle}>{t("booking.order_summary.title")}</span>
        <span className={styles.orderSummaryCount}>{t("booking.order_summary.count", { count: items.length })}</span>
        <FoldButton
          isOpen={isExpanded}
          openLabel={t("booking.order_summary.expand")}
          closeLabel={t("booking.order_summary.collapse")}
          onToggle={() => setIsFolded(isExpanded)}
        />
      </Flex>
      <Flex vertical gap="var(--spacing-xxs)">
        {isExpanded &&
          items.map((item) => (
            <OrderExperience
              key={item.key}
              experience={item.experience}
              selections={item.selections}
              discountLabel={item.discountLabel}
              error={item.error}
              onChange={onChangeItem && (() => onChangeItem(item.key))}
            />
          ))}
        {footer}
      </Flex>
    </Flex>
  )
}

OrderSummary.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      experience: PropTypes.object,
      selections: PropTypes.object,
      // The promo code's line ("Promo-code (SAVE10)"); null when the booking has no discount.
      discountLabel: PropTypes.string,
      // Why the API refused this experience; null otherwise.
      error: PropTypes.string
    })
  ).isRequired,
  footer: PropTypes.node,
  // "Change" on an experience with an error, called with its key.
  onChangeItem: PropTypes.func
}

OrderSummary.defaultProps = {
  footer: null,
  onChangeItem: null
}

export default OrderSummary
